import {useEffect, useMemo, useState} from 'react';

import API from '../api/axios';

export const CHUNK_SIZE = 1048576 * 3;

type UseChunkedUploadOptions = {
  url: string;
  fieldName?: string;
  params?: Record<string, unknown>;
  onComplete?: (response: unknown) => void;
  onError?: (error: unknown) => void;
};

export type ChunkedUpload = {
  isSubmitting: boolean;
  progress: number;
  start: (file: File) => void;
  cancel: () => void;
};

export const useChunkedUpload = ({
  url,
  fieldName = 'map',
  params = {},
  onComplete,
  onError,
}: UseChunkedUploadOptions): ChunkedUpload => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [chunkIndex, setChunkIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalChunkCount = useMemo(
    () => (file ? Math.ceil(file.size / CHUNK_SIZE) : 0),
    [file]
  );

  const progress = useMemo(
    () =>
      file ? Math.round(((chunkIndex * CHUNK_SIZE) / file.size) * 100) : 0,
    [file, chunkIndex]
  );

  const currentChunk = useMemo(
    () =>
      isSubmitting && file
        ? file.slice(chunkIndex * CHUNK_SIZE, (chunkIndex + 1) * CHUNK_SIZE)
        : null,
    [file, chunkIndex, isSubmitting]
  );

  const cancel = () => {
    setIsSubmitting(false);
    setChunkIndex(0);
    setUploadId(null);
    setFile(null);
  };

  const start = (nextFile: File) => {
    if (!nextFile.size) {
      return;
    }

    setFile(nextFile);
    setUploadId(Math.random().toString(36).slice(2));
    setChunkIndex(0);
    setIsSubmitting(true);
  };

  useEffect(() => {
    const uploadChunk = async () => {
      const activeUploadId = uploadId;
      const form = new FormData();

      if (currentChunk) {
        form.append(fieldName, currentChunk, fieldName);
      }

      let response: unknown;

      try {
        const resp = await API.post(url, form, {
          params: {
            ...params,
            fileName: activeUploadId,
            chunkIndex,
            totalChunkCount,
          },
        });
        response = resp.data;
      } catch (error) {
        onError?.(error);
        cancel();
        return;
      }

      if (chunkIndex + 1 === totalChunkCount) {
        cancel();
        onComplete?.(response);
        return;
      }

      setChunkIndex((current) =>
        current === chunkIndex ? current + 1 : current
      );
    };

    if (currentChunk?.size) {
      uploadChunk();
    }
  }, [currentChunk]);

  return {isSubmitting, progress, start, cancel};
};
